import { ChevronRight } from 'lucide-react';
import { memo, useState } from 'react';

import { Markdown } from '@apps/web/components/ui/markdown';
import {
    type V3ReasoningEntry,
    type V3ReasoningMilestoneStep,
    type V3ReasoningNarration,
    type V3ReasoningStep,
} from '@apps/web/features/phoebe_chat_v3/model/transcript';

function workBlockLabel(durationMs: number | null): string {
    if (durationMs === null) {
        return 'Worked';
    }
    return `Worked for ${Math.max(0, Math.round(durationMs / 1000))}s`;
}

function V3ReasoningNarrationBlock({
    narration,
}: {
    readonly narration: V3ReasoningNarration;
}) {
    return (
        <div className="min-w-0 border-l-2 border-[#DBE3E3] pl-3.5 text-[13px] leading-5 text-[#617578] [&_p]:my-0 [&_p]:text-[13px] [&_p]:leading-5">
            <Markdown>{narration.text}</Markdown>
        </div>
    );
}

function V3MilestoneHeader({
    step,
}: {
    readonly step: V3ReasoningMilestoneStep;
}) {
    return (
        <div className="flex min-w-0 items-center gap-2 text-[13px] leading-5 text-[#42595E]">
            <span
                aria-hidden="true"
                className="flex size-4 shrink-0 items-center justify-center rounded-full border border-[#05614F] text-[10px] leading-none font-semibold text-[#05614F]"
            >
                ✓
            </span>
            <span className="min-w-0">{step.label}</span>
            {step.detail ? (
                <span className="min-w-0 text-[#617578]">{step.detail}</span>
            ) : null}
        </div>
    );
}

function V3ReasoningStepRow({ step }: { readonly step: V3ReasoningStep }) {
    switch (step.type) {
        case 'narration':
            return <V3ReasoningNarrationBlock narration={step} />;
        case 'milestone': {
            if (step.narrations.length === 0) {
                return <V3MilestoneHeader step={step} />;
            }
            return (
                <details className="group/step">
                    <summary className="flex cursor-pointer list-none items-center gap-2 select-none [&::-webkit-details-marker]:hidden">
                        <V3MilestoneHeader step={step} />
                        <ChevronRight
                            aria-hidden="true"
                            className="size-3 shrink-0 transition-transform group-open/step:rotate-90"
                        />
                    </summary>
                    <div className="mt-1 space-y-2 py-1 pl-6">
                        {step.narrations.map((narration) => (
                            <V3ReasoningNarrationBlock
                                key={narration.id}
                                narration={narration}
                            />
                        ))}
                    </div>
                </details>
            );
        }
        default: {
            const exhaustiveStep: never = step;
            return exhaustiveStep;
        }
    }
}

export const V3ReasoningRow = memo(function V3ReasoningRow({
    entry,
}: {
    readonly entry: V3ReasoningEntry;
}) {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <details className="group max-w-[700px] text-[#617578]" open={isOpen}>
            <summary
                className="flex cursor-pointer list-none items-center gap-2 py-1 text-xs leading-4 select-none [&::-webkit-details-marker]:hidden"
                onClick={(event) => {
                    event.preventDefault();
                    setIsOpen((open) => !open);
                }}
            >
                {workBlockLabel(entry.durationMs)}
                <ChevronRight
                    aria-hidden="true"
                    className="size-3 shrink-0 transition-transform group-open:rotate-90"
                />
            </summary>
            <div className="mt-1 space-y-2 py-1">
                {entry.steps.map((step) => (
                    <V3ReasoningStepRow key={step.id} step={step} />
                ))}
            </div>
        </details>
    );
});
